// Ajustes de UI nativa (Capacitor): barra de estado, splash screen y botón
// físico "atrás" de Android. Todo no-op en web.
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { App as CapApp } from '@capacitor/app';
import { isNative, platform } from './platform';

const BRAND_NAVY = '#0A2F52';

// Ojo con el enum, que se lee al revés de lo que parece: Style.Dark es texto
// CLARO (para fondos oscuros) y Style.Light es texto OSCURO (para fondos
// claros). Así lo define @capacitor/status-bar.
//
// En iPhone la barra de estado se superpone al WebView y lo que hay debajo es
// el fondo del body (blanco en modo claro, navy oscuro en modo oscuro), así
// que el estilo tiene que seguir al TEMA: la primera tester del build 2157
// (2026-09-03) usaba modo oscuro y veía la hora en texto oscuro sobre navy.
export function estiloBarraParaTema({ oscuro }) {
  return oscuro ? Style.Dark : Style.Light;
}

function temaEsOscuro(doc = document) {
  return doc.documentElement.classList.contains('dark');
}

// Inicializa la UI nativa. Devuelve una función de cleanup. No-op en web.
export function initNativeUI() {
  if (!isNative) return () => {};

  // Marca el <html> para poder aplicar CSS específico de nativo (safe areas).
  document.documentElement.classList.add('cap-native');

  let observador = null;

  (async () => {
    try {
      if (platform === 'ios') {
        // La barra se superpone al body: el estilo sigue al tema, ahora y cada
        // vez que el usuario lo cambie (ThemeContext alterna la clase .dark).
        // async + try por dentro: si el plugin falla o no devuelve promesa, el
        // observador de abajo se instala igual (un setStyle caído no puede
        // dejar la barra pegada al tema equivocado para toda la sesión).
        const aplicar = async () => {
          try { await StatusBar.setStyle({ style: estiloBarraParaTema({ oscuro: temaEsOscuro() }) }); } catch { /* noop */ }
        };
        await aplicar();
        observador = new MutationObserver(aplicar);
        observador.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
      } else {
        // Android: franja sólida navy arriba, sin superponerse al WebView.
        // Se deja tal cual está en producción (Style.Light aquí es texto
        // oscuro sobre navy según el enum, pero no se cambia a ciegas sin
        // verlo en un teléfono; pendiente de confirmar por el fundador).
        await StatusBar.setOverlaysWebView({ overlay: false });
        await StatusBar.setBackgroundColor({ color: BRAND_NAVY });
        await StatusBar.setStyle({ style: Style.Light });
      }
    } catch {
      // setBackgroundColor/​setOverlaysWebView son sólo Android; ignorar en otros.
    }
    // Oculta el splash una vez que React ya montó (evita parpadeo en blanco).
    try { await SplashScreen.hide(); } catch { /* sin splash plugin */ }
  })();

  // Botón atrás de Android → integra con el history del SPA (popstate).
  // Si no hay a dónde volver, sale de la app.
  const backHandle = CapApp.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack || window.history.length > 1) {
      window.history.back();
    } else {
      CapApp.exitApp();
    }
  });

  return () => {
    observador?.disconnect();
    backHandle.then((h) => h.remove()).catch(() => {});
  };
}
